import Emitter from './emitter';
import { redraw, slideUp, slideDown } from './drawing';
/**
 * Module for toggling the visual state of elements.
 * @module utils/toggler
 */

/** Transition type map */
export const transitionTypes = {
  css: {
    init(toggler) {
      toggler.element.addEventListener('transitionend', event => {
        if (event.target === toggler.element) toggler.transitionEnd();
      });
    }
  },
  height: {
    init(toggler) {
      toggler.element.style.display = toggler.isOpen ? '' : 'none';
    },
    start(toggler) {
      if (toggler.isOpen) {
        slideUp(toggler.element, 300, _ => toggler.transitionEnd());
      } else {
        slideDown(toggler.element, 300, _ => toggler.transitionEnd());
      }
    },
    skip(toggler) {
      toggler.element.style.display = toggler.isOpen ? '' : 'none';
    }
  }
};

/**
 * Toggler class for toggling the visual state of an element.
 * @extends Emitter
 */
export default class Toggler extends Emitter {

  /**
   * Create a toggler.
   * @param {HTMLElement} element - The element to toggle.
   * @param {Map} settings - The toggle settings.
   */
  constructor(element, settings = {}) {
    super();

    this.element = element;
    this.isOpen = (typeof settings.open === 'boolean') ? settings.open : true;
    this.transition = (settings.transition in transitionTypes) ? settings.transition : false;

    this.transitionInit();
  }

  /**
   * Toggle.
   * @param {Boolean} isOpen - The toggle state.
   * @param {Boolean} transition - Whether to use transitions.
   */
  toggle(isOpen, transition) {
    isOpen = (typeof isOpen === 'boolean') ? isOpen : !this.isOpen;
    transition = (typeof transition === 'boolean') ? transition : true;

    if (this.isOpen !== isOpen) {
      this.isOpen = isOpen;

      if (this.transition && transition) {
        this.transitionStart();

      } else {
        this.element.classList.remove('is-before-opening');
        this.element.classList.remove('is-opening');
        this.element.classList.remove('is-before-closing');
        this.element.classList.remove('is-closing');
        this.element.classList.toggle('is-open', this.isOpen);
        this.element.classList.toggle('is-closed', !this.isOpen);

        this.transitionSkip();
      }

      this.trigger('toggle');
      this.trigger(this.isOpen ? 'open' : 'close');
    }
  }

  /**
   * Set open.
   * @param {Boolean} transition - Whether to use transitions.
   */
  open(transition) {
    this.toggle(true, transition);
  }

  /**
   * Set closed.
   * @param {Boolean} transition - Whether to use transitions.
   */
  close(transition) {
    this.toggle(false, transition);
  }

  /** 
   * Init transition.
  */
  transitionInit() {
    this.element.classList.remove('is-before-opening');
    this.element.classList.remove('is-opening');
    this.element.classList.remove('is-before-closing');
    this.element.classList.remove('is-closing');
    this.element.classList.toggle('is-open', this.isOpen);
    this.element.classList.toggle('is-closed', !this.isOpen);

    const transitionType = transitionTypes[this.transition];
    if (transitionType && transitionType.init) transitionType.init(this);
  }

  /**
   * Start transition.
  */
  transitionStart() {

    if (this.isOpen) {

      if (this.element.classList.contains('is-before-closing')) {

        this.element.classList.remove('is-before-closing');
        this.element.classList.add('is-open');

      } else if (this.element.classList.contains('is-closing')) {

        this.element.classList.remove('is-before-closing');
        this.element.classList.remove('is-closing');
        this.element.classList.add('is-opening');

      } else {

        this.element.classList.remove('is-closed');
        this.element.classList.add('is-before-opening');

        redraw(this.element);

        this.element.classList.remove('is-before-opening');
        this.element.classList.add('is-opening');

      }

    } else {

      if (this.element.classList.contains('is-before-opening')) {

        this.element.classList.remove('is-before-opening');
        this.element.classList.add('is-closed');

      } else if (this.element.classList.contains('is-opening')) {

        this.element.classList.remove('is-opening');
        this.element.classList.add('is-closing');

      } else {

        this.element.classList.remove('is-open');
        this.element.classList.add('is-before-closing');

        redraw(this.element);

        this.element.classList.remove('is-before-closing');
        this.element.classList.add('is-closing');
      }
    }

    const transitionType = transitionTypes[this.transition];
    if (transitionType && transitionType.start) {
      transitionType.start(this);
    }

    this.trigger('transitionStart');
  }

  /** 
   * End transition.
  */
  transitionEnd() {
    if (this.isOpen) {

      this.element.classList.remove('is-opening');
      this.element.classList.add('is-open');

    } else {

      this.element.classList.remove('is-closing');
      this.element.classList.add('is-closed');
    }

    const transitionType = transitionTypes[this.transition];
    if (transitionType && transitionType.end) transitionType.end(this);

    this.trigger('transitionEnd');
  }

  /** 
   * Ski[p tranistion.
  */
  transitionSkip() {
    const transitionType = transitionTypes[this.transition];
    if (transitionType && transitionType.skip) transitionType.skip(this);
  }
}



// WEBPACK FOOTER //
// ./source/scripts/utils/toggler.js